export const PERMISSION_CODES = [
    "DASHBOARD_VIEW",
    "USERS_VIEW",
    "USERS_MAP_VIEW",
    "USERS_EXPORT",
    "USERS_MODERATE",
    "USERS_DELETE",
    "RIDERS_VIEW",
    "RIDERS_EXPORT",
    "RIDER_BOOKINGS_VIEW",
    "RIDER_TRIPS_VIEW",
    "RIDER_WALLET_VIEW",
    "DRIVERS_VIEW",
    "DRIVERS_EXPORT",
    "DRIVER_RIDES_VIEW",
    "DRIVER_TRIPS_VIEW",
    "DRIVER_WALLET_VIEW",
    "DRIVERS_MODERATE",
    "DRIVER_ROAD_WORTHINESS_REVIEW",
    "DRIVER_PAYOUTS_VIEW",
    "DRIVER_PAYOUTS_MANAGE",
    "RIDE_OFFERS_VIEW",
    "RIDE_OFFERS_MANAGE",
    "RIDE_BOOKINGS_VIEW",
    "RIDE_BOOKINGS_MANAGE",
    "RIDE_BOOKINGS_REFUND",
    "TRIPS_VIEW",
    "TRIPS_MANAGE",
    "WALLETS_VIEW",
    "WALLET_ADJUST",
    "WALLET_FREEZE",
    "TRANSACTIONS_VIEW",
    "PAYMENTS_VIEW",
    "PLATFORM_WALLET_VIEW",
    "PAYOUTS_MANAGE",
    "WITHDRAWALS_VIEW",
    "WITHDRAWALS_MANAGE",
    "WALLET_OPERATIONS_VIEW",
    "WALLET_OPERATIONS_MANAGE",
    "MESSAGING_VIEW",
    "MESSAGING_MANAGE",
    "PUSH_NOTIFICATIONS_SEND",
    "ANALYTICS_VIEW",
    "AUDIT_VIEW",
    "AUDIT_SECURITY_VIEW",
    "AUDIT_EXPORT",
    "APPROVALS_VIEW",
    "APPROVALS_EXPORT",
    "APPROVALS_MANAGE",
    "FRAUD_VIEW",
    "FRAUD_MANAGE",
    "FRAUD_RUN_JOBS",
    "LIVE_MONITORING_VIEW",
] as const;

export type PermissionCode = (typeof PERMISSION_CODES)[number];

const PERMISSION_CODE_SET = new Set<string>(PERMISSION_CODES);

export const isPermissionCode = (value: string): value is PermissionCode =>
    PERMISSION_CODE_SET.has(value);

export const normalizePermissionList = (
    permissions: readonly string[] | null | undefined,
    role?: string | null
): PermissionCode[] => {
    const normalized = Array.from(
        new Set((permissions ?? []).filter(isPermissionCode))
    );

    if (normalized.length > 0) {
        return normalized;
    }

    if (role === "SUPER_ADMIN") {
        return [...PERMISSION_CODES];
    }

    return [];
};

export const hasPermission = (
    permissions: readonly string[] | null | undefined,
    permission: PermissionCode
) => normalizePermissionList(permissions).includes(permission);

export const hasAnyPermission = (
    permissions: readonly string[] | null | undefined,
    requiredPermissions: readonly PermissionCode[]
) => {
    if (requiredPermissions.length === 0) {
        return true;
    }

    const normalized = new Set(normalizePermissionList(permissions));
    return requiredPermissions.some((permission) => normalized.has(permission));
};

export const hasAllPermissions = (
    permissions: readonly string[] | null | undefined,
    requiredPermissions: readonly PermissionCode[]
) => {
    if (requiredPermissions.length === 0) {
        return true;
    }

    const normalized = new Set(normalizePermissionList(permissions));
    return requiredPermissions.every((permission) => normalized.has(permission));
};

export const formatPermissionCode = (permission: string) =>
    permission
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
